import { useState, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { CustomContainer } from "../../atoms/DependencyHelper";
import PageHeading from "../../atoms/pageHeading/PageHeading";
import FaqItem from "../../atoms/faqItem/FaqItem";
import { Accordion, AccordionItem } from "../../atoms/accordion/Accordion";
import DropDown from "../../atoms/newLineForms/dropDown/DropDown";
import {
  createMarkup,
  componentCheckSize,
} from "../../../utils/helperServices";
import styles from "./CheckoutFaq.module.scss";
import * as Analytics from "../../../services/analyticsUtag";

const CheckoutFaq = memo(({ heading, headingId, faq, customerStatus }) => {
  const isTablet = componentCheckSize() <= 992;
  const [activeAccordion, setActiveAccordion] = useState("");

  const mainHeading = faq ? faq.title : heading;
  const faqSections = faq ? faq.faqs : [];

  const [faqOptions, setFaqOptions] = useState([]);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState(0);
  const [selectedFaqId, setSelectedFaqId] = useState(null);
  const [defaultSelectedFaqId, setDefaultSelectedFaqId] = useState(null);

  /**
   * Set the zero index dropdown selected by default
   */
  useEffect(() => {
    if (faq && faq.faqs) {
      let totalFaq = faq.faqs.length;
      for (let i = 0; i < totalFaq; i++) {
        if (faq.faqs[i].faqOptions) {
          setFaqOptions(faq.faqs[i].faqOptions);
          setDefaultSelectedFaqId(faq.faqs[i].faqOptions[selectedFaqIndex].id);
          setSelectedFaqId(faq.faqs[i].faqOptions[selectedFaqIndex].id);
          break;
        }
      }
    }
  }, [faq]);

  const setActiveAccordionValue = async (eventKey) => {
    if (eventKey === activeAccordion) {
      setActiveAccordion("");
    } else {
      setActiveAccordion(eventKey);
    }
  };

  const generateClass = (eventKey) => {
    return eventKey === activeAccordion
      ? `${styles["accordion-wrapper__item"]} ${styles["accordion-wrapper__item--active"]}`
      : styles["accordion-wrapper__item"];
  };

  /**
   * Dropdown change event handler
   *
   * @usage Set the id of selected faq
   * @usage Show the content of selected faq
   * @call onChange event
   */
  const handleChange = async (event, val, index) => {
    event.persist();
    setSelectedFaqIndex(index);
    setSelectedFaqId(val);

    Analytics.link({
      ...Analytics.getUtagData([
        "page_name",
        "page_channel",
        "page_master_tab",
        "visitor_login_status",
        "visitor_customer_id",
        "visitor_id_hash",
        "visitor_msisdn_hash",
        "visitor_subscription_type",
      ]),
      page_tool_event: "use",
      page_tool_name: `checkout: lets get started: ${customerStatus}: application process`,
      filter_query: faqOptions[index]?.name,
    });
  };

  /**
   * Dropdown field reset handler
   *
   * @usage Set the default selected
   */
  const handleReset = async () => {
    setSelectedFaqIndex(0);
    setSelectedFaqId(defaultSelectedFaqId);

    Analytics.link({
      ...Analytics.getUtagData([
        "page_name",
        "page_channel",
        "page_master_tab",
        "visitor_login_status",
        "visitor_customer_id",
        "visitor_id_hash",
        "visitor_msisdn_hash",
        "visitor_subscription_type",
      ]),
      page_tool_event: "use",
      page_tool_name: `checkout: lets get started: ${customerStatus}: application process`,
      filter_query: faqOptions[0]?.name,
    });
  };

  return (
    <CustomContainer className={`${styles["main-container"]}`} id={headingId}>
      <CustomContainer className={`${styles["container"]}`}>
        <CustomContainer className={`${styles["top-wrapper"]}`}>
          <PageHeading
            text={mainHeading}
            className={styles["top-wrapper__heading"]}
          />
        </CustomContainer>

        {faqSections.length > 0 && !isTablet && (
          <CustomContainer className={`${styles["items-wrapper"]}`}>
            {faqSections.map((item, index) => (
              <FaqItem
                key={index}
                item={item}
                selectedFaqId={selectedFaqId}
                selectedFaqIndex={selectedFaqIndex}
                onChangeHandler={handleChange}
                onResetHandler={handleReset}
              />
            ))}
          </CustomContainer>
        )}

        {faqSections.length > 0 && isTablet && (
          <Accordion className={styles["accordion-wrapper"]}>
            {faqSections.map((item, index) => (
              <AccordionItem
                key={index}
                title={item.title}
                className={generateClass(item.id)}
                eventKey={item.id}
                id={item.quesId ? item.quesId : item.id}
                activeAccordion={activeAccordion}
                handleOnClick={() => setActiveAccordionValue(item.id)}
              >
                {item.faqOptions ? (
                  <>
                    <div className={`${styles["select-row"]}`}>
                      <div className={`${styles["select-group"]}`}>
                        <DropDown
                          label="Application process"
                          name={`faqSelect-${item.id}`}
                          data={item.faqOptions}
                          handleSelect={handleChange}
                          handleReset={handleReset}
                          resetRequired={true}
                          selectedId={selectedFaqId}
                          customId={`checkout-faq-select-${item.id}`}
                        />
                      </div>
                    </div>
                    <div
                      className={`${styles["accordion-wrapper__subitem"]} ${styles["select-desc"]}`}
                      dangerouslySetInnerHTML={createMarkup(
                        item.faqOptions[selectedFaqIndex]?.description
                      )}
                    ></div>
                  </>
                ) : (
                  <div
                    className={styles["accordion-wrapper__subitem"]}
                    dangerouslySetInnerHTML={createMarkup(item.description)}
                  ></div>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CustomContainer>
    </CustomContainer>
  );
});

CheckoutFaq.propTypes = {
  heading: PropTypes.string,
  headingId: PropTypes.string,
  customerStatus: PropTypes.string,
};

CheckoutFaq.defaultProps = {
  heading: "Frequently asked questions",
  headingId: "faqs",
};

export default CheckoutFaq;
